import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getClientDetails, impersonateClient, generateIntakeLink } from '../../services/api';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { ArrowLeft, Loader2, Pencil, EyeOff, Link2, Copy, Check, X } from 'lucide-react';
import ClientSettingsTab from '../../components/clients/ClientSettingsTab';
import MarketplaceMatchCard from '../../components/MarketplaceMatchCard';
import CompanyTabs from '../../components/company/CompanyTabs';

// Consultant client-detail page. The body is the shared company surface:
// Team, Departments and Company Context, the same three tabs the client sees
// on their own Settings page and at /team. Team and Departments used to be
// toolbar toggles that expanded in place above an embedded My Business, which
// meant the consultant and the client were looking at two different layouts of
// the same three things (B1).
//
// The consultant chrome is a slim toolbar: Orbit link, Impersonate, Edit.
// Scheduling a call left the header in B2. It lives on the section that is
// missing information now, inside Company Context, because you delegate a gap
// rather than a page.
const ClientDetail: React.FC = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const navigate = useNavigate();
    const { startImpersonation } = useImpersonation();

    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [intakeLink, setIntakeLink] = useState<string | null>(null);
    const [linkLoading, setLinkLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    const fetchDetails = useCallback(async () => {
        if (!clientId) return;
        try {
            setLoading(true);
            const result = await getClientDetails(clientId);
            setData(result);
        } catch (err: any) {
            console.error('Error fetching client details:', err);
            setError(err.message || 'Failed to load client details');
            toast.error('Failed to load client details');
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    const handleGenerateLink = async () => {
        if (!clientId) return;
        setLinkLoading(true);
        try {
            const res = await generateIntakeLink(clientId);
            setIntakeLink(res.url);
            setCopied(false);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to generate link');
        } finally {
            setLinkLoading(false);
        }
    };

    const handleCopyLink = async () => {
        if (!intakeLink) return;
        try {
            await navigator.clipboard.writeText(intakeLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error('Could not copy — select and copy manually.');
        }
    };

    const handleImpersonate = async () => {
        if (!clientId) return;
        try {
            const result = await impersonateClient(clientId);
            startImpersonation(result);
            localStorage.setItem('client_id', clientId);
            localStorage.setItem('user_role', 'client');
            if (result.token) {
                localStorage.setItem('token', result.token);
            }
            navigate('/dashboard');
            toast.success('Impersonation started. Viewing as client.');
        } catch (err) {
            console.error('Error impersonating client:', err);
            toast.error('Failed to impersonate client');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading client details...</span>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="text-sm text-red-500">{error || 'Client not found'}</div>
                <button
                    onClick={() => navigate('/consultant/dashboard')}
                    className="text-sm text-brand-600 hover:text-brand-700 font-medium"
                >
                    Back to Clients
                </button>
            </div>
        );
    }

    const client = data.client;

    return (
        <div>
            {/* Slim consultant toolbar — back nav + actions. The tabs below are
                the shared company surface, scoped to this client. */}
            <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
                <button
                    onClick={() => navigate('/consultant/dashboard')}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Clients
                </button>

                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={handleGenerateLink}
                        disabled={linkLoading}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors disabled:opacity-60 whitespace-nowrap"
                    >
                        {linkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                        Orbit link
                    </button>
                    <button
                        onClick={handleImpersonate}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
                    >
                        <EyeOff className="w-3.5 h-3.5" />
                        Impersonate
                    </button>
                    <button
                        onClick={() => setShowSettings(true)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
                    >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                    </button>
                </div>
            </div>

            {/* Marketplace matches for this client's dossier — renders only
                when the matcher finds existing solutions that fit. */}
            {clientId && <MarketplaceMatchCard clientId={clientId} />}

            {/* Team, Departments and Company Context, from the same component
                the client's own Settings page and /team mount. This is how AA's
                own experts and team leads are added, and how the internal
                workspace's departments are managed, without impersonation.
                Company Context is read-only: the live Orbit dock is bound to
                the signed-in user, so it cannot drive another client's session
                here — the consultant uses Orbit link or Impersonate above. */}
            {clientId && (
                <div className="mt-6">
                    <CompanyTabs
                        clientId={clientId}
                        clientName={client?.name || 'Workspace'}
                        readOnlyContext
                    />
                </div>
            )}

            {/* Orbit intake link modal */}
            {intakeLink && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl">
                        <div className="flex items-start justify-between mb-1">
                            <div className="flex items-center gap-2">
                                <Link2 className="w-5 h-5 text-brand-600" />
                                <h2 className="text-lg font-bold text-gray-900">Orbit intake link</h2>
                            </div>
                            <button onClick={() => setIntakeLink(null)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                            Share this with {client.name}. Opening it drops them straight into Orbit
                            intake with no sign-in. It works once and expires in 14 days.
                        </p>
                        <div className="flex items-center gap-2">
                            <input
                                readOnly
                                value={intakeLink}
                                onFocus={(e) => e.target.select()}
                                className="flex-1 p-2.5 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                            />
                            <button
                                onClick={handleCopyLink}
                                className="inline-flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 shrink-0 whitespace-nowrap"
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                {copied ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit client modal — the former Settings tab, now reached via the
                Edit button since the tab bar was retired. */}
            {showSettings && createPortal(
                // Portal to <body> so the overlay covers the full viewport
                // (incl. the left sidebar) instead of being clipped by an
                // ancestor's stacking/transform context.
                <div
                    className="fixed inset-0 bg-black/30 backdrop-blur-[2px] flex items-start justify-center z-[110] p-4 overflow-y-auto"
                    onClick={() => setShowSettings(false)}
                >
                    <div
                        className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-xl my-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Pencil className="w-5 h-5 text-brand-600" />
                                <h2 className="text-lg font-bold text-gray-900">Edit {client.name}</h2>
                            </div>
                            <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <ClientSettingsTab client={client} onUpdated={fetchDetails} onClose={() => setShowSettings(false)} />
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
};

export default ClientDetail;
